import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { euro, toCents, fromCents } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Pencil, Package, Layers, Tag } from "lucide-react";
import { toast } from "sonner";

export default function Catalog() {
  const { user } = useAuth();
  const isSuper = user.role === "super_admin";
  const [tab, setTab] = useState("articoli");
  const [articles, setArticles] = useState(null);
  const [kits, setKits] = useState(null);
  const [prices, setPrices] = useState(null);
  const [editArt, setEditArt] = useState(null);
  const [editPrice, setEditPrice] = useState(null);

  const load = useCallback(() => {
    api.get("/catalog/articles").then((r) => setArticles(r.data));
    api.get("/catalog/kits").then((r) => setKits(r.data));
    api.get("/catalog/price-list").then((r) => setPrices(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const artName = (id) => articles?.find((a) => a.id === id)?.name || id;

  const saveArt = async () => {
    try {
      await api.put(`/catalog/articles/${editArt.id}`, {
        sku: editArt.sku, name: editArt.name, category: editArt.category, size: editArt.size,
        description: editArt.description || "", purchase_cost_cents: editArt._cost, replacement_value_cents: editArt._repl,
        deposit_value_cents: editArt._dep, supplier: editArt.supplier || "", active: editArt.active, notes: editArt.notes || "",
      });
      toast.success("Articolo aggiornato"); setEditArt(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const savePrice = async () => {
    try {
      const scope = isSuper ? "network" : "partner";
      await api.post("/catalog/price-items", { kit_id: editPrice.kit_id, name: editPrice.name, price_cents: editPrice._price, suggested_deposit_cents: editPrice._dep, scope });
      toast.success("Listino aggiornato"); setEditPrice(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Catalogo" subtitle="Articoli, kit e listino prezzi" />
      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList>
          <TabsTrigger value="articoli" data-testid="tab-articoli">Articoli</TabsTrigger>
          <TabsTrigger value="kit" data-testid="tab-kit">Kit</TabsTrigger>
          <TabsTrigger value="listini" data-testid="tab-listini">Listino</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "articoli" && (!articles ? <Loading /> : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>
              <th className="text-left px-4 py-3">Articolo</th><th className="text-left px-4 py-3">SKU</th>
              <th className="text-right px-4 py-3">Costo</th><th className="text-right px-4 py-3">Sostituzione</th>
              <th className="text-right px-4 py-3">Cauzione</th><th className="px-4 py-3"></th></tr></thead>
            <tbody className="divide-y">
              {articles.map((a) => (
                <tr key={a.id} data-testid={`article-${a.sku}`}>
                  <td className="px-4 py-3"><p className="font-semibold text-[#0B2447]">{a.name}</p><p className="text-xs text-slate-400">{a.category} · {a.size}</p></td>
                  <td className="px-4 py-3 text-slate-500">{a.sku}</td>
                  <td className="px-4 py-3 text-right">{euro(a.purchase_cost_cents)}</td>
                  <td className="px-4 py-3 text-right">{euro(a.replacement_value_cents)}</td>
                  <td className="px-4 py-3 text-right font-semibold">{euro(a.deposit_value_cents)}</td>
                  <td className="px-4 py-3 text-right">{isSuper && <Button size="icon" variant="ghost" data-testid={`edit-article-${a.sku}`} onClick={() => setEditArt({ ...a, _cost: a.purchase_cost_cents, _repl: a.replacement_value_cents, _dep: a.deposit_value_cents })}><Pencil size={15} /></Button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ))}

      {tab === "kit" && (!kits ? <Loading /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {kits.map((k) => (
            <Card key={k.id} className="p-5" data-testid={`kit-${k.code}`}>
              <p className="font-display font-bold text-[#0B2447] flex items-center gap-2"><Layers size={16} className="text-[#0EA5C4]" />{k.name}</p>
              <p className="text-xs text-slate-400 mb-3">{k.code}</p>
              <div className="space-y-1">
                {k.components.map((c, i) => <div key={i} className="flex justify-between text-sm"><span className="text-slate-600">{artName(c.article_id)}</span><span className="font-semibold text-[#0B2447]">×{c.qty}</span></div>)}
              </div>
            </Card>
          ))}
        </div>
      ))}

      {tab === "listini" && (!prices ? <Loading /> : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>
              <th className="text-left px-4 py-3">Voce</th><th className="text-right px-4 py-3">Prezzo cambio</th>
              <th className="text-right px-4 py-3">Deposito suggerito</th><th className="px-4 py-3"></th><th className="px-4 py-3"></th></tr></thead>
            <tbody className="divide-y">
              {prices.map((p) => (
                <tr key={p.kit_id} data-testid={`price-${p.kit_id}`}>
                  <td className="px-4 py-3 font-semibold text-[#0B2447]">{p.name}</td>
                  <td className="px-4 py-3 text-right font-bold">{euro(p.price_cents)}</td>
                  <td className="px-4 py-3 text-right">{euro(p.suggested_deposit_cents)}</td>
                  <td className="px-4 py-3 text-center">{p.has_override && <span className="text-xs px-2 py-0.5 rounded-full bg-[#E4F5FB] text-[#0EA5C4] font-semibold">Override partner</span>}</td>
                  <td className="px-4 py-3 text-right"><Button size="icon" variant="ghost" data-testid={`edit-price-${p.kit_id}`} onClick={() => setEditPrice({ kit_id: p.kit_id, name: p.name, _price: p.price_cents, _dep: p.suggested_deposit_cents })}><Pencil size={15} /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
          {!isSuper && <p className="px-4 py-3 text-xs text-slate-400">Le modifiche creano un override di prezzo per il tuo Partner.</p>}
        </Card>
      ))}

      <Dialog open={!!editArt} onOpenChange={(o) => !o && setEditArt(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Modifica articolo</DialogTitle></DialogHeader>
          {editArt && (
            <div className="space-y-3">
              <div><Label>Nome</Label><Input className="mt-1" value={editArt.name} onChange={(e) => setEditArt({ ...editArt, name: e.target.value })} /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Costo €</Label><Input data-testid="art-cost" className="mt-1" defaultValue={fromCents(editArt._cost)} onChange={(e) => setEditArt({ ...editArt, _cost: toCents(e.target.value) })} /></div>
                <div><Label>Sostituz. €</Label><Input className="mt-1" defaultValue={fromCents(editArt._repl)} onChange={(e) => setEditArt({ ...editArt, _repl: toCents(e.target.value) })} /></div>
                <div><Label>Cauzione €</Label><Input data-testid="art-dep" className="mt-1" defaultValue={fromCents(editArt._dep)} onChange={(e) => setEditArt({ ...editArt, _dep: toCents(e.target.value) })} /></div>
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={saveArt} data-testid="save-article" className="bg-[#0B2447]">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editPrice} onOpenChange={(o) => !o && setEditPrice(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Modifica listino · {editPrice?.name}</DialogTitle></DialogHeader>
          {editPrice && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Prezzo cambio €</Label><Input data-testid="price-value" className="mt-1" defaultValue={fromCents(editPrice._price)} onChange={(e) => setEditPrice({ ...editPrice, _price: toCents(e.target.value) })} /></div>
              <div><Label>Deposito suggerito €</Label><Input className="mt-1" defaultValue={fromCents(editPrice._dep)} onChange={(e) => setEditPrice({ ...editPrice, _dep: toCents(e.target.value) })} /></div>
            </div>
          )}
          <DialogFooter><Button onClick={savePrice} data-testid="save-price" className="bg-[#0B2447]">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
